import { ChartGranularity } from "../../../models/bidAnalytics";
import { OrderSummaryPoint, OrderSummaryRow, StatsGranularity } from "../../../models/orderAnalytics";
import { toNum } from "../../bids/analytics/format";

export type GroupKey = "success" | "failed" | "inProgress" | "attention";

export type GroupMeta = {
    key: GroupKey;
    label: string;
    color: string;
    jColor: "success" | "danger" | "primary" | "warning";
};

/** Группы статусов: успешные — Completed; неуспешные — Failed, Refunded; в работе — New … WaitingForBuyer;
 * требуют внимания — ManualMode с причиной, Cancelling (ManualMode без причины — «в работе») */
export const GROUPS: GroupMeta[] = [
    { key: "success", label: "Успешные", color: "#2e7d32", jColor: "success" },
    { key: "failed", label: "Неуспешные", color: "#c62828", jColor: "danger" },
    { key: "inProgress", label: "В работе", color: "#1565c0", jColor: "primary" },
    { key: "attention", label: "Требуют внимания", color: "#ef6c00", jColor: "warning" },
];

export type GroupCard = GroupMeta & { count: number; sum: number; percent: number };

function groupCount(row: OrderSummaryRow, key: GroupKey): number {
    return toNum(row[`${key}Count`]);
}

/**
 * Проценты долей с точностью digits, в сумме ровно 100 (метод наибольшего остатка);
 * при нулевом итоге — нули
 */
export function percentsSummingTo100(counts: number[], digits = 1): number[] {
    const total = counts.reduce((a, b) => a + b, 0);
    if (total <= 0) {
        return counts.map(() => 0);
    }

    const scale = 10 ** digits;
    const exact = counts.map((c) => (c * 100 * scale) / total);
    const units = exact.map(Math.floor);
    let rest = 100 * scale - units.reduce((a, b) => a + b, 0);
    const byRemainder = exact
        .map((value, index) => ({ index, remainder: value - units[index] }))
        .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    for (const { index } of byRemainder) {
        if (rest <= 0) {
            break;
        }
        units[index] += 1;
        rest -= 1;
    }

    return units.map((u) => u / scale);
}

/** Карточки четырёх групп: число, % от всех предметов строки (в сумме 100), сумма в копейках */
export function groupCards(row: OrderSummaryRow): GroupCard[] {
    const counts = GROUPS.map((g) => groupCount(row, g.key));
    const percents = percentsSummingTo100(counts);
    return GROUPS.map((g, i) => ({
        ...g,
        count: counts[i],
        sum: toNum(row[`${g.key}Sum`]),
        percent: percents[i],
    }));
}

export type GameBars = {
    labels: string[];
    series: Array<GroupMeta & { data: number[] }>;
};

/** Столбцы по играм: серии — группы статусов, для stacked bar */
export function toGameBars(byGame: OrderSummaryRow[]): GameBars {
    return {
        labels: byGame.map((r) => r.gameName),
        series: GROUPS.map((g) => ({ ...g, data: byGame.map((r) => groupCount(r, g.key)) })),
    };
}

export type SummaryDatasetPoint = {
    x: Date;
    totalCount: number;
    successCount: number;
    failedCount: number;
    /** null — в интервале нет завершённых ордеров (точка пропускается на графике) */
    successRate: number | null;
};

/** Время с бэка в UTC; суффикс зоны бывает не всегда */
export function parseUtc(value: string): Date {
    return new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
}

/** Ряд для графиков по времени, по возрастанию; бэк отдаёт время UTC, иногда без суффикса зоны */
export function toSummaryDataset(series: OrderSummaryPoint[]): SummaryDatasetPoint[] {
    return series
        .map((p) => {
            const successCount = toNum(p.successCount);
            const failedCount = toNum(p.failedCount);
            return {
                x: parseUtc(p.bucketUtc),
                totalCount: toNum(p.totalCount),
                successCount,
                failedCount,
                successRate: successCount + failedCount > 0 ? toNum(p.successRate) : null,
            };
        })
        .sort((a, b) => a.x.getTime() - b.x.getTime());
}

const GRANULARITY: Record<ChartGranularity, StatsGranularity> = {
    hour: StatsGranularity.Hour,
    day: StatsGranularity.Day,
    month: StatsGranularity.Month,
};

export function toStatsGranularity(granularity: ChartGranularity): StatsGranularity {
    return GRANULARITY[granularity];
}
